import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Menu, 
  LogOut, 
  User,
  ChevronDown,
  Globe,
  Moon,
  Sun
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { useTheme } from '../../hooks/useTheme';
import './Header.css';

const Header = ({ onMenuClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentLanguage, changeLanguage, availableLanguages, isRTL } = useLanguage();
  const { isDark, changeTheme } = useTheme();
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setShowLangMenu(false);
  };

  const currentLang = availableLanguages.find(l => l.code === currentLanguage);

  const profilePicUrl = user?.profile_picture_url;

  return (
    <header className={`header ${isRTL ? 'rtl' : ''}`}>
      <div className="header-left">
        <button 
          className="menu-button"
          onClick={onMenuClick}
          aria-label="Toggle menu"
        >
          <Menu size={24} />
        </button>
      </div>

      <div className="header-right">
        <button
          className="dropdown-trigger theme-toggle-btn"
          onClick={() => changeTheme(isDark ? 'light' : 'dark')}
          title={isDark ? t('settings.themeLight', 'Light mode') : t('settings.themeDark', 'Dark mode')}
          aria-label={isDark ? 'Enable light mode' : 'Enable dark mode'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Language Selector */}
        <div className="dropdown" ref={langMenuRef}>
          <button 
            className="dropdown-trigger"
            onClick={() => setShowLangMenu(!showLangMenu)}
          >
            <Globe size={18} />
            <span className="dropdown-label">{currentLang?.nativeName}</span>
            <ChevronDown size={16} className={showLangMenu ? 'rotate' : ''} />
          </button>
          
          {showLangMenu && (
            <div className="dropdown-menu">
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  className={`dropdown-item ${lang.code === currentLanguage ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.nativeName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="dropdown" ref={userMenuRef}>
          <button 
            className="dropdown-trigger user-trigger"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt="Profile"
                className="user-avatar-img"
              />
            ) : (
              <div className="user-avatar">
                <User size={18} />
              </div>
            )}
            <span className="dropdown-label">{user?.email || t('common.unknown')}</span>
            <ChevronDown size={16} className={showUserMenu ? 'rotate' : ''} />
          </button>
          
          {showUserMenu && (
            <div className="dropdown-menu">
              <button
                className="dropdown-item danger"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
